import moment from "moment";
import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Badge,
  Button,
  OverlayTrigger,
  Table,
  Tooltip,
  Dropdown
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import {
  BiSort,
  BiChevronUp,
  BiChevronDown,
  BiPencil,
  BiSolidHourglass,
  BiX,
  BiCheck,
  BiMinus
} from "react-icons/bi";
import { BsEye, BsThreeDotsVertical, BsPerson } from "react-icons/bs";
import useHasPermission from "../../../../core/hooks/useHasPermission";
import { FcFinePrint } from "react-icons/fc";

import Pagination from "../../../../components/Pagination/Pagination";
import { LoaderContext } from "../../../../App";
import {
  DEFAULT_ITEMS_PER_PAGE,
  FORM_BUILDER_LISTING_URL,
  LS_ACCESS_TOKEN_KEY,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC,
  STATUS
} from "../../../../core/constants/common";
import DataCollectionEventService from "../../../../core/service/DataCollectionEventService";
import FormService from "../../../../core/service/FormService";
import Utils from "../../../../core/utilities/Utils";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import "../Forms.scss";

function ResponsesTab({ t, id, self, qualitativeDataStatus, currentRoute, searchKey, onSearchChange, filters = {}, activeTab }) {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const authToken = localStorage.getItem(LS_ACCESS_TOKEN_KEY);
  const { belongsToPrevilegedGroup, hasDCERevokePermission } = useHasPermission();

  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState(SORT_ORDER_ASC);
  const [sortField, setSortField] = useState('parentBranch');
  const [forms, setForms] = useState([]);
  const [formsQual, setFormsQual] = useState([]);
  const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);
  const [submissionIdToRevoke, setSubmissionIdToRevoke] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [logsData, setLogsData] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  const isInitialMount = useRef(true);
  const prevFiltersRef = useRef(null);
  const prevSearchKeyRef = useRef(null);
  const prevActiveTabRef = useRef(null);

  useEffect(() => {
    setExpandedRows({});
  }, [filters.childStatus, searchKey]);

  // Combine effects to prevent duplicate calls
  useEffect(() => {
    if (!id) return;

    const isTabActive = activeTab === 'responses';
    const wasTabActive = prevActiveTabRef.current === 'responses';
    const tabBecameActive = !wasTabActive && isTabActive;

    if (qualitativeDataStatus) {
      if (isInitialMount.current || tabBecameActive) {
        isInitialMount.current = false;
        prevActiveTabRef.current = activeTab;
        if (isTabActive) {
          getSubmissionsQual(0, itemsPerPage);
        }
      } else {
        // Update prevActiveTabRef even if tab is not active
        prevActiveTabRef.current = activeTab;
      }
      return;
    }

    // On initial mount or when tab becomes active, fetch data
    if (isInitialMount.current || tabBecameActive) {
      isInitialMount.current = false;
      prevActiveTabRef.current = activeTab;
      prevFiltersRef.current = filters.childStatus;
      prevSearchKeyRef.current = searchKey;
      if (isTabActive) {
        getSubmissions(0, itemsPerPage, searchKey, sortField);
      }
      return;
    }

    // Update prevActiveTabRef even if tab is not active
    prevActiveTabRef.current = activeTab;

    // Only refetch if tab is active and filters or searchKey changed
    if (!isTabActive) return;

    // Check if filters or searchKey actually changed
    const filtersChanged = prevFiltersRef.current !== filters.childStatus;
    const searchKeyChanged = prevSearchKeyRef.current !== searchKey;

    prevFiltersRef.current = filters.childStatus;
    prevSearchKeyRef.current = searchKey;

    // Refetch only if something actually changed
    if (filtersChanged || searchKeyChanged) {
      setCurrentPage(1);
      getSubmissions(0, itemsPerPage, searchKey, sortField);
    }
  }, [id, qualitativeDataStatus, searchKey, filters.childStatus, activeTab]);

  const getSubmissionsQual = (pageNumber, itemsPerPageVal) => {
    let perPage = '';
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }

    DataCollectionEventService.getShareDetails(id,
      pageNumber,
      perPage,
    ).then((response) => {
      const totalPages = parseFloat(response.headers["x-total-count"]);
      setItemTotalCount(totalPages);
      setFormsQual([...response.data]);
    })
      .catch((error) => {
      });
  };

  const getSubmissions = (pageNumber, itemsPerPageVal, key = "", sortField) => {
    if (qualitativeDataStatus) {
      return;
    }
    setLoaderDisplay(true);
    let perPage = '';
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }
    let sort = sortField + "," + order;

    // Build status filter from childStatusFilters
    let statusParam = null;
    if (filters.childStatus) {
      statusParam = filters.childStatus;
    }

    FormService.getSubmissions(
      id,
      pageNumber,
      perPage,
      sort,
      key,
      false, // toReview = false for Responses tab
      statusParam
    ).then((response) => {
      const totalPages = parseFloat(response.headers["x-total-count"]);
      setItemTotalCount(totalPages);
      setForms([...response.data]);
      setLoaderDisplay(false);
    })
      .catch((error) => {
        setLoaderDisplay(false);
      });
  };

  const handleSort = (val) => {
    const newOrder = sortField === val && order === "asc" ? "desc" : "asc";
    setOrder(newOrder);
    setSortField(val);
    getSubmissions(0, itemsPerPage, searchKey, val);
  };

  const viewSubmission = (action, formId = "", sId, key) => {
    const formType = "ASSESSMENT";
    const string = Utils.generateUrlParamFormCreate(
      action,
      authToken,
      formType,
      0,
      formId,
      key,
      sId,
      null,
      { route: currentRoute, qParams: { tab: 'res' } }
    );
    const encodedDat = Utils.encodeBase64(string);
    window.location.href = FORM_BUILDER_LISTING_URL + `/?q=${encodedDat}`;
  };

  const renderStatusBadge = (item) => {
    if (item.tokenStatus === 'REVOKED') {
      return (
        <Badge bg="badge-Rejected" className="rounded-pill">
          {STATUS['NO_PERMISSION']}
        </Badge>
      );
    }
    switch (item.status) {
      case "COMPLETED":
        return (
          <Badge bg="badge-Published" className="rounded-pill">
            {STATUS[item.status]}
          </Badge>
        );
      case "IN_PROGRESS":
        return (
          <Badge bg="badge-Draft" className="rounded-pill">
            {STATUS[item.status]}
          </Badge>
        );
      case "DRAFT":
        return (
          <Badge bg="badge-New" className="rounded-pill">
            {STATUS['NOT_STARTED']}
          </Badge>
        );
      case "PENDING_APPROVAL":
        const approvalLevel = item.pendingApprovalLevel;
        const statusText = approvalLevel 
          ? t(`PendingApprovalLevel${approvalLevel}.label`) || `Pending Approval at Level ${approvalLevel}`
          : t("PendingApproval.label");
        return (
          <Badge bg="badge-Accepted" className="rounded-pill">
            {statusText}
          </Badge>
        );
      case "CORRECTION_PENDING":
        return (
          <Badge bg="badge-Corrected" className="rounded-pill">
            {STATUS[item.status]}
          </Badge>
        );
      case "NOT_SHARED":
        return (
          <Badge bg="badge-New" className="rounded-pill">
            {STATUS[item.status]}
          </Badge>
        );
      default:
        return (
          <Badge bg="badge-New" className="rounded-pill">
            {STATUS[item.status] || item.status}
          </Badge>
        );
    }
  };

  const renderDataOwners = (emails) => {
    if (!emails) return '';
    const emailString = Array.isArray(emails) ? emails.join(',') : emails.toString();
    if (!emailString.trim()) return '';

    const emailList = emailString.split(',').map(email => email.trim()).filter(email => email);

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0px', alignItems: 'center' }}>
        {emailList.map((email, idx) => (
          <span key={idx}>
            <span style={{ display: 'inline-block' }}>{email}</span>
            {idx < emailList.length - 1 && <span style={{ marginLeft: '1px', marginRight: '4px' }}>,</span>}
          </span>
        ))}
      </div>
    );
  };

  const toggleRowExpansion = async (rowId, formDataId) => {
    const isCurrentlyExpanded = expandedRows[rowId];

    if (!isCurrentlyExpanded && formDataId && !logsData[rowId]) {
      setLoaderDisplay(true);
      try {
        const response = await FormService.getFormDataLogs(formDataId);

        if (response && response.status === 200) {
          let logsArray = [];
          if (Array.isArray(response.data)) {
            logsArray = response.data;
          } else if (response.data && Array.isArray(response.data.data)) {
            logsArray = response.data.data;
          } else if (response.data) {
            logsArray = [response.data];
          }

          if (logsArray.length > 0) {
            setLogsData(prev => ({
              ...prev,
              [rowId]: logsArray
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoaderDisplay(false);
      }
    }

    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const mapLogToWorkflowHistory = (log) => {
    let status = log.newStatus || '';
    const approvalLevel = Number(log.approvalLevel);
    if (status === 'PENDING' && (approvalLevel === 3 || approvalLevel === 2)) {
      status = 'PENDING_APPROVAL';
    }
    let statusLabel = STATUS[status] || status;

    if (status === 'PENDING_APPROVAL' && log.approvalLevel) {
      statusLabel = t(`PendingApprovalLevel${log.approvalLevel}.label`) || `Pending Approval at Level ${log.approvalLevel}`;
    } else if (status === 'PENDING' && log.approvalLevel) {
      statusLabel = t(`PendingApprovalLevel${log.approvalLevel}.label`) || `Pending Approval at Level ${log.approvalLevel}`;
    } else if (status.startsWith('PENDING_APPROVAL_LEVEL_')) {
      const level = status.replace('PENDING_APPROVAL_LEVEL_', '');
      statusLabel = t(`PendingApprovalLevel${level}.label`) || `Pending Approval at Level ${level}`;
    } else if (status === 'PENDING') {
      statusLabel = t("Pending.label") || "Pending";
    } else if (status === 'APPROVED' && log.approvalLevel) {
      statusLabel = t(`ApprovedApprovalLevel${log.approvalLevel}.label`) || `Approved at Level ${log.approvalLevel}`;
    } else if (status.startsWith('APPROVED_LEVEL_')) {
      const level = status.replace('APPROVED_LEVEL_', '');
      statusLabel = t(`ApprovedApprovalLevel${level}.label`) || `Approved at Level ${level}`;
    } else if (status === 'APPROVED') {
      statusLabel = t("Approved.label") || "Approved";
    } else if (status === 'REJECTED' && log.approvalLevel) {
      statusLabel = t(`RejectedApprovalLevel${log.approvalLevel}.label`) || `Rejected at Level ${log.approvalLevel}`;
    } else if (status.startsWith('REJECTED_LEVEL_')) {
      const level = status.replace('REJECTED_LEVEL_', '');
      statusLabel = t(`RejectedApprovalLevel${level}.label`) || `Rejected at Level ${level}`;
    } else if (status === 'REJECTED') {
      statusLabel = t("Rejected.label") || "Rejected";
    }

    const isPendingApproval =
      status === 'PENDING_APPROVAL' || status.startsWith('PENDING_APPROVAL_LEVEL_');

    let userName = '';
    let userEmail = '';
    if (isPendingApproval) {
      if (log.approver) {
        userEmail = log.approver.email || '';
        if (log.approver.fullName) {
          userName = log.approver.fullName;
        } else if (userEmail) {
          userName = userEmail;
        }
      }
      if (!userEmail) {
        userEmail = log.approverEmail || log.changedBy?.email || log.changedByEmail || '';
      }
      if (!userName) {
        if (log.changedBy?.fullName) {
          userName = log.changedBy.fullName;
        } else if (userEmail) {
          userName = userEmail;
        } else if (log.changedByEmail) {
          userName = log.changedByEmail;
        } else {
          userName = log.createdBy || 'System';
        }
      }
    } else if (log.changedBy && log.changedBy.fullName) {
      userName = log.changedBy.fullName;
      userEmail = log.changedBy.email || log.changedByEmail || '';
    } else if (log.changedBy && log.changedBy.email) {
      userName = log.changedBy.email;
      userEmail = log.changedBy.email;
    } else if (log.changedByEmail) {
      userName = log.changedByEmail;
      userEmail = log.changedByEmail;
    } else {
      userName = log.createdBy || 'System';
    }

    return {
      status: status,
      user: userName,
      userEmail,
      date: log.changedAt || log.createdDate,
      statusLabel: statusLabel,
      changeReason: log.changeReason || '',
      approvalLevel: log.approvalLevel
    };
  };

  const renderWorkflowHistory = (item, rowId) => {
    const logs = logsData[rowId];
    const isLoading = loadingLogs[rowId];

    if (isLoading) {
      return (
        <div className="workflow-history-container">
          <div className="text-center p-3">
            <span>{t("Loading.label") || "Loading..."}</span>
          </div>
        </div>
      );
    }

    if (!logs || !Array.isArray(logs) || logs.length === 0) {
      return (
        <div className="workflow-history-container">
          <div className="text-center p-3">
            <span>{t("NoLogsFound.label") || "No logs found"}</span>
          </div>
        </div>
      );
    }

    const sortedLogs = [...logs].sort((a, b) => {
      const dateA = new Date(a.changedAt || a.createdDate);
      const dateB = new Date(b.changedAt || b.createdDate);
      return dateB - dateA;
    });

    let workflowHistory = sortedLogs.map(mapLogToWorkflowHistory);
    workflowHistory = workflowHistory.filter(step => step.status !== 'PENDING');

    const consolidatedHistory = [];
    for (let i = 0; i < workflowHistory.length; i++) {
      const currentStep = workflowHistory[i];
      if (currentStep.status === 'IN_PROGRESS' &&
        i > 0 &&
        workflowHistory[i - 1].status === 'IN_PROGRESS') {
        continue;
      }
      consolidatedHistory.push(currentStep);
    }

    workflowHistory = consolidatedHistory;

    return (
      <div className="workflow-history-container">
        {workflowHistory.map((step, index) => (
          <div key={index} className="workflow-step">
            <div className="workflow-step-content">
              <div className="workflow-step-indicator">
                {step.status === 'COMPLETED' ? (
                  <div className="workflow-indicator badge bg-badge-Published">
                    <BiCheck size={16} />
                  </div>
                ) : step.status === 'APPROVED' ? (
                  <div className="workflow-indicator badge bg-badge-Approved">
                    <BiCheck size={15} />
                  </div>
                ) : step.status === 'REJECTED' ? (
                  <div className="workflow-indicator badge bg-badge-Rejected">
                    <BiX size={15} />
                  </div>
                ) : step.status === 'CORRECTION_PENDING' || step.status === 'CORRECTED' ? (
                  <div className="workflow-indicator badge bg-badge-Corrected">
                    <BiPencil className="workflow-icon-pencil" />
                  </div>
                ) : step.status === 'PENDING' ||
                  step.status === 'PENDING_APPROVAL' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_1' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_2' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_3' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_4' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_5' ? (
                  <div className="workflow-indicator badge bg-badge-Accepted">
                    <BiSolidHourglass />
                  </div>
                ) : (
                  <div className="workflow-indicator badge bg-badge-Draft">
                    <BiMinus size={15} />
                  </div>
                )}
              </div>
              <div className="workflow-step-details">
                <Badge
                  bg={
                    (step.status === 'COMPLETED') ? 'badge-Published' :
                      step.status === 'APPROVED' ? 'badge-Approved' :
                        step.status === 'REJECTED' ? 'badge-Rejected' :
                          step.status === 'CORRECTION_PENDING' || step.status === 'CORRECTED' ? 'badge-Corrected' :
                            step.status === 'DRAFT' ? 'badge-New' :
                              step.status === 'IN_PROGRESS' ? 'badge-Draft' :
                                step.status === 'PENDING' ||
                                  step.status === 'PENDING_APPROVAL' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_1' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_2' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_3' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_4' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_5' ? 'badge-Accepted' :
                                  'badge-Invited'
                  }
                  className="rounded-pill workflow-status-badge"
                >
                  {step.statusLabel}
                </Badge>

                <div className="workflow-step-meta">
                  <div className="workflow-step-meta">
                    <BsPerson className="workflow-user-icon" />
                    {step.userEmail && step.userEmail !== step.user ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`workflow-user-tooltip-${index}`}>{step.userEmail}</Tooltip>}
                      >
                        <span className="workflow-user-name">{step.user}</span>
                      </OverlayTrigger>
                    ) : (
                      <span className="workflow-user-name">{step.user}</span>
                    )}
                    <span className="workflow-date">
                      {moment(step.date).format('MMM DD YYYY, h:mm a')}
                    </span>
                  </div>
                  {step.changeReason && (
                    <div className="workflow-change-reason">
                      <div>{step.changeReason}</div>
                    </div>
                  )}
                </div>

              </div>
            </div>
            {index < workflowHistory.length - 1 && (
              <div className="workflow-connector"></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStatusBadgeWithDropdown = (item) => {
    if (item.tokenStatus === 'REVOKED') {
      return renderStatusBadge(item);
    }

    const currentStatus = item.status || 'DRAFT';
    const rowId = item.id;
    let statusLabel = STATUS[currentStatus] || currentStatus;

    if (currentStatus === 'PENDING_APPROVAL') {
      const approvalLevel = item.pendingApprovalLevel;
      statusLabel = approvalLevel
        ? t(`PendingApprovalLevel${approvalLevel}.label`) || `Pending Approval at Level ${approvalLevel}`
        : t("PendingApproval.label");
    } else if (currentStatus === 'DRAFT') {
      statusLabel = t("NotStarted.label") || 'Not Started';
    }

    let badgeColor = 'badge-Invited';
    if (currentStatus === 'CORRECTION_PENDING' || currentStatus === 'CORRECTED') {
      badgeColor = 'badge-Corrected';
    } else if (currentStatus === 'REJECTED') {
      badgeColor = 'badge-Rejected';
    } else if (currentStatus === 'COMPLETED' || currentStatus === 'APPROVED') {
      badgeColor = 'badge-Published';
    } else if (currentStatus === 'DRAFT' || currentStatus === 'NOT_SHARED') {
      badgeColor = 'badge-New';
    } else if (currentStatus === 'IN_PROGRESS') {
      badgeColor = 'badge-Draft';
    } else if (currentStatus === 'PENDING_APPROVAL' ||
      currentStatus === 'PENDING_APPROVAL_LEVEL_1' ||
      currentStatus === 'PENDING_APPROVAL_LEVEL_2' ||
      currentStatus === 'PENDING_APPROVAL_LEVEL_3') {
      badgeColor = 'badge-Accepted';
    }

    return (
      <div className="status-badge-container">
        <Badge
          bg={badgeColor}
          className="rounded-pill status-badge-clickable"
          onClick={() => toggleRowExpansion(rowId, item.id)}
        >
          {statusLabel}
          {expandedRows[rowId] ? (
            <BiChevronUp className="ms-1" />
          ) : (
            <BiChevronDown className="ms-1" />
          )}
        </Badge>
      </div>
    );
  };

  const revokeSubmission = (submissionId) => {
    if (!submissionId) return;
    setLoaderDisplay(true);
    FormService.revokeSubmission(submissionId).then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200 && res.status !== 204) return;
      
      getSubmissions(currentPage, itemsPerPage, searchKey, sortField);
    }).catch(() => {
      setLoaderDisplay(false);
    });
  };

  const promptRevokeSubmission = (submissionId) => {
    if (!submissionId) return;
    setSubmissionIdToRevoke(submissionId);
    setShowRevokeConfirmation(true);
  };

  const handleRevokeConfirmation = (isConfirmed) => {
    setShowRevokeConfirmation(false);
    if (!isConfirmed || !submissionIdToRevoke) {
      setSubmissionIdToRevoke(null);
      return;
    }

    revokeSubmission(submissionIdToRevoke);
    setSubmissionIdToRevoke(null);
  };

  return (
    <div className="List_Table_01">
      <div className="card List_table-card01">
        {qualitativeDataStatus ?
          <Table responsive="sm">
            <thead>
              <tr>
                <th >{t('Users.label')}</th>
                <th>{t("SharedDate.label")}</th>
                <th>{t("LastModified.label")}</th>
                <th>{t("Status.label")}</th>
              </tr>
            </thead>
            <tbody>
              {formsQual.length > 0 ? (
                formsQual.map((item, index) => (
                  <tr key={item.id}>
                    <td>{item.user.name}</td>
                    <td>{moment(item.sharedOn).format("MMMM DD, YYYY")}</td>
                    <td>{item.lastModifiedDate ? moment(item.sharedOn).format("MMMM DD, YYYY") : ''}</td>
                    <td>
                      {
                        item.lastModifiedDate === null
                          ?
                          <span className="badge bg-badge-New rounded-pill">{t("NotStarted.label")}</span>
                          :
                          item.progressCount['COMPLETED'] === item.progressCount['TOTAL']
                            ?
                            <span className="badge bg-badge-Finalized rounded-pill">{t("Completed.label")}</span>
                            :
                            <span className="badge bg-badge-Inprogress rounded-pill">{t("InProgress.label")}</span>
                      }
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    <div className="Nodatafound_box">
                      <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          :
          <Table responsive>
            <thead>
              <tr>
                {self === 'true' ? (
                  <>
                    <th className="white-nowrap">
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          handleSort("parentBranch");
                        }}
                      >
                        {t("ParentEntity.label") || "Parent Entity"}
                        <BiSort />
                      </a>
                    </th>
                    <th className="white-nowrap">
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          handleSort("branch");
                        }}
                      >
                        {t("ChildEntity.label")}
                        <BiSort />
                      </a>
                    </th>
                    <th className="white-nowrap">{t('DataOwners.label')}</th>
                  </>
                ) : (
                  <th className="white-nowrap">{t("Supplier.label")}</th>
                )}
                <th className="white-nowrap">{t("SharedDate.label")}</th>
                {/* <th>{t("ExpiryDate.label")}</th> */}
                <th className="white-nowrap">
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleSort("submissionDate");
                    }}
                  >
                    {t("SubmittedDate.label")}
                    <BiSort />
                  </a>
                </th>
                <th className="text-center">
                  {/* <Link to="#" onClick={() => handleSort("status")}>
                    {t("Status.label")}
                    <BiSort />
                  </Link> */}
                  {t("Status.label")}
                </th>
                <th className="text-end" />
              </tr>
            </thead>
            <tbody>
              {forms.length > 0 ? (
                forms.map((item) => {
                  const rowId = item.id;
                  const isExpanded = expandedRows[rowId];
                  return (
                    <React.Fragment key={item.submissionId}>
                      <tr>
                        {self === 'true' ? (
                          <>
                            <td>
                              {item.branch?.parent
                                ? item.branch.parent.name
                                : item.branch?.name || '-'}
                            </td>
                            <td>
                              {item.branch?.parent
                                ? item.branch.name
                                : '-'}
                            </td>
                            <td>{renderDataOwners(item.branch?.emails)}</td>
                          </>
                        ) : (
                          <td>{item.submittedByCompany?.name}</td>
                        )}
                        <td className="white-nowrap">{item.assessment?.sharedOn ? moment(item.assessment?.sharedOn).format("MMM DD, YYYY") : ''}</td>
                        <td className="white-nowrap">
                          {item.submissionDate
                            ? moment(item.submissionDate).format(
                              "MMM DD, YYYY, h:mm a"
                            )
                            : "-"}
                        </td>
                        <td className="text-center">{renderStatusBadgeWithDropdown(item)}</td>
                        <td className="text-end">
                          <div className="d-inline-flex align-items-center gap-1">
                            {
                              item.tokenStatus !== 'REVOKED' &&
                              <OverlayTrigger
                                placement="auto"
                                overlay={<Tooltip id="tooltip-like">{t("ViewResponses.label")}</Tooltip>} >
                                <Button
                                  variant="blank_outline"
                                  onClick={() => viewSubmission(
                                    "SUBMIT",
                                    item.form.id,
                                    item.submissionId,
                                    item.token
                                  )}>
                                  <BsEye />
                                </Button>
                              </OverlayTrigger>
                            }
                            {
                              belongsToPrevilegedGroup() &&
                              item.status === "COMPLETED" &&
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="blank_outline"
                                  className="px-2 h-100"
                                  id={`resp-actions-${item.submissionId}`}>
                                  <BsThreeDotsVertical />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  {
                                    hasDCERevokePermission() &&
                                    <Dropdown.Item onClick={() => promptRevokeSubmission(item.id)}>
                                      {t("RevokeSubmission.label")}
                                    </Dropdown.Item>
                                  }
                                </Dropdown.Menu>
                              </Dropdown>
                            }
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="workflow-history-row">
                          <td colSpan={self === 'true' ? "7" : "6"} className="workflow-history-cell">
                            {renderWorkflowHistory(item, rowId)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={self === 'true' ? "8" : "7"} className="text-center">
                    <div className="Nodatafound_box">
                      <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>}
        {qualitativeDataStatus ?
          <div className="p-2 List_footer_count">
            <Pagination
              itemsTotalCount={itemsTotalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              alwaysShown={true}
              setItemsPerPage={setItemsPerPage}
              pageClicked={(pageNum, perPage) => {
                getSubmissionsQual(pageNum, perPage);
              }}
            />
          </div>
          :
          <div className="p-2 List_footer_count">
            <Pagination
              itemsTotalCount={itemsTotalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              alwaysShown={true}
              setItemsPerPage={setItemsPerPage}
              pageClicked={(pageNum, perPage) => {
                getSubmissions(pageNum, perPage, searchKey, sortField);
              }}
            />
          </div>
        }
      </div>
      {showRevokeConfirmation && (
        <ConfirmationModal
          modalShow={true}
          title={t("RevokeSubmission.label")}
          messageText={t("RevokeSubmission.confirmationMessage.label")}
          callbackModal={handleRevokeConfirmation}
          confirmBtn={t("Confirm.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}
    </div>
  );
}

export default withTranslation()(ResponsesTab);

